import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MapSearchComponent} from '../map/map-search/map-search.component';
import {SearchTermDefinitionInf} from '../../interfaces/common/search/search-term-definition-inf';
import {UserPrivilegeService} from '../../services/common/user-privilege/user-privilege.service';
import * as _ from 'lodash';
import {OfflineMapDiagramComponent} from './offline-map-diagram/offline-map-diagram.component';
import {OfflineNetworkService} from '../services/offline/network/offline-network.service';
import {OfflineNodeService} from '../services/offline/node/offline-node.service';
import {OfflineVoipPeerService} from '../services/offline/voip-peer/offline-voip-peer.service';
import {OfflineNetworkApiService} from '../../services/api/network/offline/network/offline-network-api.service';
import {OneTimeConfirmationDialogService} from '../../dialogs/one-time-confirmation-dialog/service/one-time-confirmation-dialog.service';
import {OfflinePeerConnectionService} from '../services/offline/peer-connection/offline-peer-connection.service';
import {OfflineMapService} from '../services/map/offline-map/offline-map.service';
import {OfflineConnectionService} from '../services/offline/connection/offline-connection.service';
import {DataItemToVisualsMappersOfflineService} from '../services/offline-map/data-item-to-visuals-mappers-offline/data-item-to-visuals-mappers-offline.service';
import {LicenseFeaturesService} from '../../services/common/license-features/license-features.service';

@Component({
  selector: 'app-offline-map',
  templateUrl: './offline-map.component.html',
  styleUrls: ['./offline-map.component.scss']
})
export class OfflineMapComponent implements OnInit, OnDestroy {

  constructor(private userPrivilege: UserPrivilegeService,
              private offlineNodeService: OfflineNodeService,
              private offlineNetworkApiService: OfflineNetworkApiService,
              private dataItemToVisualsMappersOfflineService: DataItemToVisualsMappersOfflineService,
              private oneTimeConfirmationDialogService: OneTimeConfirmationDialogService,
              private offlineConnectionService: OfflineConnectionService,
              private offlinePconService: OfflinePeerConnectionService,
              private offlineMapService: OfflineMapService,
              private offlineVoipPeerService: OfflineVoipPeerService,
              private offlineNetworkService: OfflineNetworkService,
              private licenseFeaturesService: LicenseFeaturesService) { }

  @ViewChild(OfflineMapDiagramComponent) mapDiagram;
  mapSearchComponent = MapSearchComponent;

  searchData;
  searchTerms: SearchTermDefinitionInf[] = [
    { term: 'name', operator: ':', displayName: 'Name'},
    { term: 'administrative state', operator: '=', displayName: 'Administrative State' },
    { term: 'operative state', operator: '=', displayName: 'Operative State' },
    { term: 'elements', operator: '=', displayName: 'Elements' }
  ];

  diagramApi: any;
  isAuthorized: boolean;
  diagramSelection;
  isTestRouteDisabled: boolean;
  refreshObservable;
  manualRefreshObservable;
  removeDraggedSvgObservable;
  drawTestRouteObservable;

  readonly testRouteStoreKey = 'test-route-store-key';

  ngOnInit(): void {
    this.isAuthorized = this.userPrivilege.isAuthorized();
    this.licenseFeaturesService.isFeatureSupported('testRoute').subscribe((isFeatureSupported) => {
      this.isTestRouteDisabled = !isFeatureSupported;
    });

    this.setSearchOptions();

    this.refreshObservable = this.offlineMapService.onRefresh().subscribe(() => {
      this.refreshMap();
      this.offlineMapService.refreshSummary();
    });

    this.manualRefreshObservable = this.offlineMapService.onManualRefresh().subscribe(() => {
      this.manualRefreshMap();
      this.offlineMapService.refreshSummary();
    });

    this.removeDraggedSvgObservable = this.offlineMapService.onRemoveDraggedSvg().subscribe(() => {
      this.mapDiagram.removeDraggedSvg();
    });

    this.drawTestRouteObservable = this.offlineMapService.onDrawTestRoute().subscribe((paths) => {
      if (paths !== null && Array.isArray(paths) && paths.length > 0) {
        this.diagramApi.drawPath(paths);
      } else {
        this.diagramApi.clearPath();
      }
    });
  }

  private drawPath(paths): void {
    if (!this.diagramApi) {
      return;
    }

    if (paths !== null && Array.isArray(paths) && paths.length > 0) {
      this.diagramApi.drawPath(paths);
    } else {
      this.diagramApi.clearPath();
    }
  }

  private clearAllPaths(): void {
    if (this.diagramApi) {
      this.diagramApi.clearPath();
    }
  }

  ngOnDestroy(): void {
    this.refreshObservable.unsubscribe();
    this.removeDraggedSvgObservable.unsubscribe();
    this.drawTestRouteObservable.unsubscribe();
    this.manualRefreshObservable.unsubscribe();
  }

  editItem(): void {
    if (this.isSingleNodeSelection()) {
      if (this.diagramApi.getShapeSelection()[0].dataItem.productType === 'THIRD_PARTY') {
        this.offlineNodeService.editThirdPartyDevice(this.diagramApi.getShapeSelection()[0].dataItem.id);
      } else {
        this.offlineNodeService.editNode(this.diagramApi.getShapeSelection()[0].dataItem.id);
      }
    } else if (this.isSingleConnectionSelection()) {
      this.offlineConnectionService.editConnection(this.diagramApi.getConnectionSelection()[0].dataItem.id);
    } else if (this.isSingleVoipPeerSelection()) {
      this.offlineVoipPeerService.editVoipPeer(this.diagramApi.getShapeSelection()[0].dataItem.id);
    } else if (this.isSinglePeerConnectionSelection()) {
      if (this.diagramApi.getConnectionSelection()[0].dataItem.peerConnectionNodeType === 'Virtual') {
        this.offlinePconService.editVirtualPCon(this.diagramApi.getConnectionSelection()[0].dataItem.id);
      } else {
        this.offlinePconService.editPcon(this.diagramApi.getConnectionSelection()[0].dataItem.id);
      }
    } else if (this.isSingleClusterSelection()) {
      const onSuccess = (cluster) => {
        const clusterLabel = this.dataItemToVisualsMappersOfflineService.prepareLabelForDiagram(cluster.name);
        this.diagramApi.updateCluster(cluster.id, cluster.name, clusterLabel);
      };
      this.offlineNetworkService.editCluster(this.diagramApi.getShapeSelection()[0], onSuccess);
    }
  }

  refreshMap(): void {
    this.offlineNetworkApiService.clearOfflineMapData();
    this.mapDiagram.updateDiagramData();
  }

  manualRefreshMap(): void {
    this.offlineNetworkApiService.clearOfflineMapData();
    this.mapDiagram.manualRefresh();
  }

  deleteItem(): void {
    if (this.isSingleNodeSelection()) {
      this.offlineNodeService.deleteNode(this.diagramApi.getShapeSelection()[0].dataItem);
    } else if (this.isSingleConnectionSelection()) {
      this.offlineConnectionService.deleteConnection(this.diagramApi.getConnectionSelection()[0].dataItem);
    } else if (this.isSingleVoipPeerSelection()) {
      this.offlineVoipPeerService.deleteVoipPeer(this.diagramApi.getShapeSelection()[0].dataItem);
    } else if (this.isSinglePeerConnectionSelection()) {
      this.offlinePconService.deletePcon(this.diagramApi.getConnectionSelection()[0].dataItem);
    }
  }

  onSearch(data): void {
    let searchData;

    if (!_.isEmpty(data.searchString)) {
      searchData = data.searchString;
    } else if (!_.isNil(data.filter) && !_.isEmpty(data.filter)) {
      searchData = data.filter;
    } else {
      searchData = '';
    }

    this.mapDiagram.searchRefresh(searchData);
  }

  isDeleteItemDisabled(): boolean {
    if (this.diagramApi && this.diagramApi.isSingleSelection()) {
      if (this.isSinglePeerConnectionSelection() || this.isSingleNodeSelection() || this.isSingleConnectionSelection()) {
        return false;
      }
      if (this.isSingleVoipPeerSelection() && !this.isVoipPeerConnected(this.diagramApi.getShapeSelection()[0].dataItem)) {
        return false;
      }
    }
    return true;
  }

  isConnectionsCluster(): boolean {
    return this.diagramApi.getSelection()[0] &&
      this.diagramApi.getSelection()[0].connectionType === 'cluster';
  }

  isSingleSelection(): boolean {
    return this.diagramApi && this.diagramApi.isSingleSelection();
  }

  diagramItemAdded(droppedItem: any): void {
    if (droppedItem.type === 'node') {
      this.offlineNodeService.addNode(droppedItem);
    } else if (droppedItem.type === 'thirdPartyDevice') {
      this.offlineNodeService.addThirdPartyDevice(droppedItem);
    } else {
      this.offlineVoipPeerService.addVoipPeer(droppedItem);
    }
  }

  onDiagramSelect($event: any): void {
    this.diagramSelection = $event;
  }

  createConnection(draggedEdgeObject): void {
    this.offlineNetworkService.dragOfflineEdge(
      draggedEdgeObject,
      () => {
        this.diagramApi.removeEdges([draggedEdgeObject.id]);
      }
    );
  }

  lockItem(): void {
    if (this.isOnlyNodeSelected()){
      this.offlineNodeService.lockNodes(this.diagramApi.getShapeSelection().map(node => node.dataItem.id));
    }else{
      this.lockPCons();
    }
  }

  isLockDisabled(): boolean {
    return false;
  }

  lockPConsForIncoming(): void {
    this.offlinePconService.lockIncomingPCons(this.diagramApi.getConnectionSelection().map(item => item.dataItem.id));
  }

  isOnlyPConsSelected(): boolean {
    if (!this.diagramApi || this.diagramApi.getSelection().length === 0) { return false; }

    return this.diagramApi.getSelection().every(item => item.connectionType === 'peerConnection');
  }

  lockPConsForOutgoing(): void {
    this.offlinePconService.lockOutgoingPCons(this.diagramApi.getConnectionSelection().map(item => item.dataItem.id));
  }

  selectedItemAdminState(): string {
    return this.diagramApi &&
    this.diagramApi.getConnectionSelection() &&
    this.diagramApi.getConnectionSelection()[0] &&
    this.diagramApi.getConnectionSelection()[0].dataItem ?
      this.diagramApi.getConnectionSelection()[0].dataItem.adminState : null;
  }

  isUnlockDisabled(): boolean {
    return false;
  }

  unlockItem(): void {
    if (this.isOnlyNodeSelected()){
      if (this.isLockNodeDisabledFn(this.diagramApi.getShapeSelection()[0].dataItem)) {
        this.offlineNodeService.unlockNodes(this.diagramApi.getShapeSelection().map(node => node.dataItem.id));
      }
    }else{
      this.unlockPCons();
    }
  }

  addConnection(): void {
    if (this.isSingleNodeSelection()) {
      if (this.diagramApi.getShapeSelection()[0].dataItem.productType !== 'THIRD_PARTY') {
        this.offlineConnectionService.addConnection(this.diagramApi.getShapeSelection()[0].dataItem.id);
      } else {
        this.offlineConnectionService.addConnection(undefined, this.diagramApi.getShapeSelection()[0].dataItem.id);
      }
    } else {
      this.offlineConnectionService.addConnection();
    }
  }

  dragConnectionMode(): void {
    this.oneTimeConfirmationDialogService.showPopup('DragConnectionExplanation',
      'Click and drag the mouse from one element to the other.',
      () => {
        this.diagramApi.addEdgeMode();
      });
  }

  testRoute(): void {
    this.oneTimeConfirmationDialogService.showPopup(this.testRouteStoreKey,
      'Test Route is also available from the peer connection\'s right click action menu', () => {
        const mapSelection = this.diagramApi.getConnectionSelection();
        const selectedPeerConnection = mapSelection.length === 1 &&
        mapSelection[0].connectionType === 'peerConnection' ?
          mapSelection[0].dataItem.id : null;

        this.offlinePconService.testRoute(selectedPeerConnection);
      });
  }

  importNodesFromTopology(): void {
    this.offlineNodeService.importNodesFromTopology();
  }

  importFromTopology(): void {
    this.offlineNetworkService.importFromTopology();
  }

  clearOfflineMap(): void {
    this.offlineNetworkService.clearOfflineMap();
  }

  isAnyElementsOnMap(): boolean {
    return this.diagramApi && this.diagramApi.getData().nodes.length === 0;
  }

  private setSearchOptions(): void {
    const adminStateOptions = [
      {text: 'LOCKED', value: 'LOCKED'},
      {text: 'LOCKED INCOMING', value: 'LOCKED_INCOMING'},
      {text: 'LOCKED OUTGOING', value: 'LOCKED_OUTGOING'},
      {text: 'UNLOCKED', value: 'UNLOCKED'}
    ];

    const operStateOptions = [
      {text: 'Available', value: 'Available'},
      {text: 'Unavailable', value: 'Unavailable'},
      {text: 'Predeleted', value: 'Predeleted'},
      {text: 'Unknown', value: 'Unknown'},
      {text: 'Unroutable', value: 'Unroutable'}
    ];

    const elementsOptions = [
      {text: 'Nodes', value: 'Nodes'},
      {text: 'Voip Peers', value: 'Voip Peers'},
      {text: 'Connections', value: 'Connections'},
      {text: 'Peer Connections', value: 'Peer Connections'}
    ];

    this.searchData = {
      adminStateOptions,
      operStateOptions,
      elementsOptions
    };
  }

  private isSingleNodeSelection(): boolean {
    return this.diagramApi && this.diagramApi.isSingleSelection() &&
      this.diagramApi.getShapeSelection().length === 1 &&
      this.diagramApi.getShapeSelection()[0].shapeType === 'node';
  }

  private isSingleConnectionSelection(): boolean {
    return this.diagramApi && this.diagramApi.isSingleSelection() &&
      this.diagramApi.getConnectionSelection().length === 1 &&
      this.diagramApi.getConnectionSelection()[0].connectionType === 'connection';
  }

  private isSingleVoipPeerSelection(): boolean {
    return this.diagramApi && this.diagramApi.isSingleSelection() &&
      this.diagramApi.getShapeSelection().length === 1 &&
      this.diagramApi.getShapeSelection()[0].shapeType === 'voipPeer';
  }

  private isSinglePeerConnectionSelection(): boolean {
    return this.diagramApi && this.diagramApi.isSingleSelection() &&
      this.diagramApi.getConnectionSelection().length === 1 &&
      this.diagramApi.getConnectionSelection()[0].connectionType === 'peerConnection';
  }

  private isSingleClusterSelection(): boolean {
    return this.diagramApi && this.diagramApi.isSingleSelection() &&
      this.diagramApi.getShapeSelection().length === 1 &&
      this.diagramApi.getShapeSelection()[0].shapeType === 'cluster';
  }

  private isVoipPeerConnected(dataItem): boolean {
    const pConsOfVoipPeer = this.diagramApi.getData().edgesArray.filter((edge) => {

      return edge.connectionType === 'peerConnection' && edge.dataItem.voipPeerId === dataItem.id;
    });
    return pConsOfVoipPeer.length > 0;
  }

  private isLockNodeDisabledFn(dataItem): boolean {
    return dataItem.adminState.toLowerCase() === 'locked';
  }

  isOnlyNodeSelected(): boolean {
    if (!this.diagramApi || this.diagramApi.getSelection().length === 0) { return false; }

    return this.diagramApi.getSelection()
      .every(item => item.shapeType === 'node' || item.shapeType === 'thirdPartyDevice');
  }

  lockPCons(): void {
    this.offlinePconService.lockPcons(this.diagramApi.getConnectionSelection().map(item => item.dataItem.id));
  }

  unlockPCons(): void {
    this.offlinePconService.unlockPcons(this.diagramApi.getConnectionSelection().map(item => item.dataItem.id));
  }

}
